import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  BackendDeleteResponse,
  BackendUpdateResponse,
  Datastore,
  DatastoreDocument,
  OrderByDirection,
  uniqWith,
} from '@freelancer/datastore';
import {
  Category,
  MembershipBenefitsCollection,
  Skill,
  SkillsCollection,
  UsersCollection,
  UserSkill,
  UserSkillsCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { isDefined, toNumber } from '@freelancer/utils';
import { JobApi, RoleApi } from 'api-typings/common/common';
import * as Rx from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  startWith,
  take,
} from 'rxjs/operators';

@Component({
  template: `
    <app-skills-edit-modal-desktop-view
      [busySavingSkills$]="busySavingSkills$"
      [categories$]="categories$"
      [errorSaving$]="errorSaving$"
      [filteredSkills$]="filteredSkills$"
      [loadingSearchedForSkills$]="loadingSearchedForSkills$"
      [matchingJobCount$]="matchingJobCount$"
      [maxSkillsLimit$]="maxSkillsLimit$"
      [selectedCategory$]="selectedCategory$"
      [selectedSkills$]="selectedSkills$"
      [showExceededLimitErrorMessage$]="showExceededLimitErrorMessage$"
      [showSaveButton$]="showSaveButton$"
      [skillFormGroup]="skillFormGroup"
      [skillSearchControl]="skillSearchControl"
      (deselectedSkill)="handleDeselectedSkill($event)"
      (deselectedSkillSelection)="handleDeselectedSkillSelection($event)"
      (savedSkills)="handleSaveSkills()"
      (selectedCategory)="handleSelectedCategory($event)"
      (selectedSkill)="handleSelectedSkill($event)"
    ></app-skills-edit-modal-desktop-view>

    <ng-container *flFeature="Feature.MEMBERSHIPS">
      <app-skills-limit-reached
        *ngIf="showSkillsLimitReachedDialog$ | async"
        (close)="handleLimitReachedDialogCloseClicked($event)"
        (undefinedMembershipPackage)="handleUndefinedMembershipPackage()"
      ></app-skills-limit-reached>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsEditModalComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  Feature = Feature;
  FontColor = FontColor;
  TextSize = TextSize;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  Margin = Margin;

  private readonly OTHERS_CATEGORY: Category = { name: 'Others', id: 99 };
  private readonly DELOITTE_SKILLS_FILTER: ReadonlyArray<number> = [88, 252]; // Dating and XXX skills

  private _busySavingSkills$ = new Rx.BehaviorSubject<boolean>(false);
  private _changesMade$ = new Rx.BehaviorSubject<boolean>(false);
  private _errorSaving$ = new Rx.Subject<boolean>();
  private _selectedCategory$ = new Rx.Subject<Category>();
  private _selectedSkills$ = new Rx.BehaviorSubject<ReadonlyArray<Skill>>([]);
  private _loadingSearchedForSkills$ = new Rx.Subject<boolean>();
  private _showSkillsLimitReachedDialogSubject$ = new Rx.BehaviorSubject<
    boolean
  >(false);
  private _selectedSkillSubject$ = new Rx.Subject<Skill>();
  private _undefinedMembershipPackageSubject$ = new Rx.Subject();
  private _maxSkillsSubject$ = new Rx.Subject<number>();

  busySavingSkills$ = this._busySavingSkills$.asObservable();
  errorSaving$ = this._errorSaving$.asObservable();
  selectedCategory$ = this._selectedCategory$.asObservable();
  selectedSkills$ = this._selectedSkills$.asObservable();
  loadingSearchedForSkills$ = this._loadingSearchedForSkills$.asObservable();
  showSkillsLimitReachedDialog$ = this._showSkillsLimitReachedDialogSubject$.asObservable();

  userDocument: DatastoreDocument<UsersCollection>;
  userSkillsDocument: DatastoreDocument<UserSkillsCollection>;
  selectedSkillThatExceededTheLimitSubscription?: Rx.Subscription;
  showSkillsLimitReachedDialogSubscription?: Rx.Subscription;
  skillsInitialiseSubscription?: Rx.Subscription;

  skillFormGroup = new FormGroup({});
  skillSearchControl = new FormControl('');

  categories$: Rx.Observable<ReadonlyArray<Category>>;
  filteredSkills$: Rx.Observable<ReadonlyArray<Skill>>;
  matchingJobCount$: Rx.Observable<number>;
  maxSkillsLimit$: Rx.Observable<number>;
  overMaxSkillsLimit$: Rx.Observable<boolean>;
  showSaveButton$: Rx.Observable<boolean>;
  userId$: Rx.Observable<number>;
  isFreelancer$: Rx.Observable<boolean>;
  showExceededLimitErrorMessage$: Rx.Observable<boolean>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private fb: FormBuilder,
    private modalRef: ModalRef<SkillsEditModalComponent>,
  ) {}

  ngOnInit() {
    const allSkillsCollection$ = this.datastore
      .collection<SkillsCollection>('skills', q =>
        q.orderBy('activeProjectCount', OrderByDirection.DESC),
      )
      .valueChanges()
      .pipe(publishReplay(1), refCount());
    this.userSkillsDocument = this.datastore.document<UserSkillsCollection>(
      'userSkills',
    );

    const membershipBenefitsDocument$ = this.datastore.document<
      MembershipBenefitsCollection
    >('membershipBenefits', 'skills_limit');

    this.userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    this.userDocument = this.datastore.document<UsersCollection>(
      'users',
      this.userId$,
    );

    // filtering out xxx and dating skills for deloitte
    // TODO: filter these on backend (T131917)
    const allSkills$ = Rx.combineLatest([
      allSkillsCollection$,
      this.userDocument.valueChanges(),
    ]).pipe(
      map(([allSkills, user]) =>
        user.isDeloitteDcUser
          ? allSkills.filter(
              (job: JobApi) => !this.DELOITTE_SKILLS_FILTER.includes(job.id),
            )
          : allSkills,
      ),
      publishReplay(1),
      refCount(),
    );

    const userDocument = this.datastore.document<UsersCollection>(
      'users',
      this.userId$,
    );

    this.isFreelancer$ = userDocument
      .valueChanges()
      .pipe(map(user => user.role === RoleApi.FREELANCER));

    // Set a form group of all skills and pre-fill the selected skills.
    this.skillsInitialiseSubscription = Rx.combineLatest([
      this.userSkillsDocument.valueChanges().pipe(distinctUntilChanged()),
      allSkills$.pipe(distinctUntilChanged()),
      this.busySavingSkills$.pipe(distinctUntilChanged()),
    ]).subscribe(([usersSkills, skills, savingSkills]) => {
      // Don't reinitialise after saving the skills as we close the form after this.
      if (savingSkills) {
        return;
      }

      const selectedSkills: Skill[] = [];

      skills.forEach(skill => {
        const hasSkill = usersSkills.skills.includes(skill.id);
        if (hasSkill) {
          selectedSkills.push(skill);
        }

        this.skillFormGroup.addControl(skill.seoUrl, this.fb.control(hasSkill));
      });

      this._selectedSkills$.next(selectedSkills);
    });

    this.maxSkillsLimit$ = Rx.combineLatest([
      membershipBenefitsDocument$.valueChanges(),
      this._maxSkillsSubject$.pipe(startWith(undefined)),
    ]).pipe(map(([benefit, maxSkills]) => maxSkills || benefit.value));

    this.overMaxSkillsLimit$ = Rx.combineLatest([
      this.maxSkillsLimit$,
      this._selectedSkills$,
    ]).pipe(
      map(([maxSkills, selectedSkills]) => selectedSkills.length > maxSkills),
    );

    this.showSkillsLimitReachedDialogSubscription = Rx.combineLatest([
      this.isFreelancer$,
      this.overMaxSkillsLimit$,
      this._selectedSkillSubject$.pipe(filter(isDefined)),
    ]).subscribe(([isFreelancer, overMaxSkillsLimit]) => {
      if (isFreelancer && overMaxSkillsLimit) {
        this._showSkillsLimitReachedDialogSubject$.next(true);
      } else if (!isFreelancer && overMaxSkillsLimit) {
        this.handleUndefinedMembershipPackage();
      }
    });

    this.skillSearchControl.valueChanges.subscribe(() => {
      this._loadingSearchedForSkills$.next(true);
    });

    this.showSaveButton$ = Rx.combineLatest([
      this.overMaxSkillsLimit$,
      this._changesMade$,
    ]).pipe(
      map(
        ([overMaxSkillCount, changesMade]) => !overMaxSkillCount && changesMade,
      ),
    );

    this.matchingJobCount$ = this.selectedSkills$.pipe(
      map(skills =>
        skills.reduce((prev, cur) => prev + cur.activeProjectCount, 0),
      ),
    );

    this.filteredSkills$ = Rx.combineLatest([
      allSkills$,
      this.selectedCategory$.pipe(startWith(undefined)),
      this.skillSearchControl.valueChanges.pipe(
        startWith(undefined),
        debounceTime(500),
      ),
    ]).pipe(
      map(([skills, selectedCategory, searchInputText]) => {
        // If the user has used the skill search input, prioritise over the
        // selected category. Search on both the skill name and category name.
        if (searchInputText) {
          const searchTerm = searchInputText.toLowerCase();
          const filteredSkills = skills.filter(
            skill =>
              (skill.category &&
                skill.category.name &&
                skill.category.name.toLowerCase().includes(searchTerm)) ||
              skill.name.toLowerCase().includes(searchTerm),
          );
          this._loadingSearchedForSkills$.next(false);
          return filteredSkills;
        }
        // If a category has been selected return all skills with that category.
        if (selectedCategory) {
          const filteredSkills = skills.filter(
            skill =>
              skill.category && skill.category.id === selectedCategory.id,
          );
          this._loadingSearchedForSkills$.next(false);
          return filteredSkills;
        }

        this._loadingSearchedForSkills$.next(false);
        // The user has not selected a category or used the search box.
        return [];
      }),
    );

    this.categories$ = allSkills$.pipe(
      // Take only unique categories from all active skills.
      map(skills => [
        ...uniqWith(
          skills.map(skill => skill.category).filter(isDefined),
          (t1, t2) => t1.id === t2.id,
        )
          // Remove the 'Others' category and order by id.
          .filter(category => category.id !== this.OTHERS_CATEGORY.id)
          .sort((a, b) => a.id - b.id),
        // Appends the 'Others' category to the end of the list.
        this.OTHERS_CATEGORY,
      ]),
      publishReplay(1),
      refCount(),
    );

    this.selectedSkillThatExceededTheLimitSubscription = Rx.combineLatest([
      this._selectedSkillSubject$,
      this.overMaxSkillsLimit$,
      this.showSkillsLimitReachedDialog$,
    ]).subscribe(
      ([selectedSkill, overMaxSkillsLimit, showLimitReachedModal]) => {
        if (overMaxSkillsLimit && showLimitReachedModal) {
          this.handleDeselectedSkillSelection(selectedSkill);
        }
      },
    );

    this.showExceededLimitErrorMessage$ = Rx.combineLatest([
      this._showSkillsLimitReachedDialogSubject$,
      this._undefinedMembershipPackageSubject$,
    ]).pipe(
      map(([showSkillsLimitReachedDialog]) => !showSkillsLimitReachedDialog),
    );
  }

  handleUndefinedMembershipPackage(): void {
    this._undefinedMembershipPackageSubject$.next();
  }

  handleSelectedCategory(selectedCategory: Category): void {
    this._selectedCategory$.next(selectedCategory);

    // Clear out the input text when selecting a category.
    this.skillSearchControl.reset(undefined);
  }

  handleSelectedSkill(selectedSkill: Skill): void {
    this._selectedSkillSubject$.next(selectedSkill);
    this._changesMade$.next(true);

    this.selectedSkills$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        this._selectedSkills$.next([...skills, selectedSkill]);
      });
  }

  handleDeselectedSkill(deselectedSkill: Skill): void {
    this._changesMade$.next(true);

    this.selectedSkills$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        this._selectedSkills$.next(
          skills.filter(skill => skill !== deselectedSkill),
        );
      });
  }

  handleSaveSkills(): void {
    this._busySavingSkills$.next(true);
    this._errorSaving$.next(false);

    Rx.combineLatest([this.userId$, this.selectedSkills$])
      .pipe(take(1))
      .toPromise()
      .then(([userId, selectedSkills]) => {
        const skillIds = selectedSkills.map(skill => skill.id);
        const userSkill: UserSkill = {
          id: userId,
          skills: skillIds,
        };

        // Only update the skills if the user has at least 1 skill selected.
        if (skillIds.length > 0) {
          this.userSkillsDocument
            .update(userSkill)
            .then(response => this.handleSaveResponse(response));
        } else {
          this.userSkillsDocument
            .remove()
            .then(response => this.handleSaveResponse(response));
        }
      });
  }

  handleSaveResponse(
    response:
      | BackendDeleteResponse<UserSkillsCollection>
      | BackendUpdateResponse<UserSkillsCollection>,
  ): void {
    this._busySavingSkills$.next(false);
    if (response.status === 'error') {
      // TODO: implement a toast error message when live.
      this._errorSaving$.next(true);
      return;
    }

    // Close the modal with a boolean of if any changes were made. This is used
    // to determine if the user profile top skills needs to be updated.
    this.modalRef.close(this._changesMade$.getValue());
  }

  handleDeselectedSkillSelection(deselectedSkill: Skill): void {
    const skill = this.skillFormGroup.get(deselectedSkill.seoUrl);
    if (skill) {
      skill.setValue(null);
    }
    this.handleDeselectedSkill(deselectedSkill);
  }

  /**
   * Handles the membership upsell close with firing
   * the _maxSkillsSubject$ if the newMaxSkillsLimit was given
   *
   * @param newMaxSkillsLimit This is the new max skills limit
   *                          after updating the membership
   */
  handleLimitReachedDialogCloseClicked(newMaxSkillsLimit?: number): void {
    this._showSkillsLimitReachedDialogSubject$.next(false);
    if (newMaxSkillsLimit) {
      this._maxSkillsSubject$.next(newMaxSkillsLimit);
    }
  }

  ngOnDestroy(): void {
    if (this.showSkillsLimitReachedDialogSubscription) {
      this.showSkillsLimitReachedDialogSubscription.unsubscribe();
    }
    if (this.selectedSkillThatExceededTheLimitSubscription) {
      this.selectedSkillThatExceededTheLimitSubscription.unsubscribe();
    }
    if (this.skillsInitialiseSubscription) {
      this.skillsInitialiseSubscription.unsubscribe();
    }
  }
}
